import React, { useRef } from 'react';
import { Send, ImagePlus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { MAX_PHOTO_SIZE_BYTES, MAX_PHOTO_SIZE_MB } from '../../constants';
import { compressImage } from '../../lib/compressImage';

const MessageInput = ({ input, setInput, pendingImage, setPendingImage, onSend }) => {
  const fileRef = useRef(null);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error('Image too large', {
        description: `Please choose an image under ${MAX_PHOTO_SIZE_MB}MB.`,
      });
      return;
    }
    try { setPendingImage(await compressImage(file)); }
    catch { toast.error("Couldn't read that image."); }
  };

  const placeholder = pendingImage ? 'Add a caption (optional)...' : 'Type a message...';

  return (
    <form onSubmit={onSend} className="p-4 border-t border-purple-900/40" data-testid="chat-form">
      {pendingImage && (
        <div className="mb-2 relative inline-block">
          <img
            src={pendingImage}
            alt="preview"
            className="h-24 w-24 object-cover rounded-lg border border-purple-700"
          />
          <button
            type="button"
            onClick={() => setPendingImage(null)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-fnl-pink rounded-full flex items-center justify-center text-white shadow-md hover:bg-fnl-pink/90"
            aria-label="Remove image"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleImagePick}
          className="hidden"
          data-testid="chat-file-input"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          variant="ghost"
          className="h-11 w-11 p-0 bg-fnl-purple-light/40 hover:bg-fnl-purple-light text-fnl-pink shrink-0"
          aria-label="Attach image"
          title="Attach image"
        >
          <ImagePlus className="w-5 h-5" />
        </Button>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="bg-white border-0 h-11 flex-1"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          className="bg-fnl-pink hover:bg-fnl-pink h-11 px-5"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
};

export default MessageInput;
